import { computed, Ref, ref } from "vue";
import { useCustomMutation } from "@/api/graphqlClient/useCustomMutation";
import {
  TokenAuthenticate,
  TokenAuthenticate_tokenAuthenticate_Authentication,
  TokenAuthenticateVariables,
  TokenAuthenticate_tokenAuthenticate_ResponseErrors_errors_InvalidOtp,
} from "@/api/authentication/__generated__/TokenAuthenticate";
import { tokenAuthenticateGql } from "@/api/authentication/login";
import { parseGqlResponse } from "@/shared/utils/graphql/responseParser";
import insertItemsIf from "@dynameyes/js-utils/insertItemsIf";
import { HideIfErrorCode } from "@/shared/utils/graphql/errorHandler";
import find from "lodash/find";
import { apiErrorCodes } from "@/shared/utils/constants";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import routeNames from "@/shared/router/routeNames";
import { loginSimpleStore } from "@/shared/store/simpleStore/loginData";
import { useAccountPermissionQuery } from "@/shared/composables/api/useAccountPermission";
import { useUserStatus } from "@/shared/composables/useUserStatus";
import { VerificationStatus } from "@/../__generated__/globalTypes";

export function useTokenAuthenticate(): {
  tokenAuthenticateLoading?: Ref<boolean>;
  tokenAuthenticate: (
    captcha?,
    showError?: boolean,
    username?: string,
    password?: string,
    code?: string,
    isLoginOtp?: boolean,
    isRegister?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any;
  otpRequired: Ref<boolean>;
} {
  const store = useStore();
  const router = useRouter();
  const { fetchAccountPermission, accountPermissionLoading } =
    useAccountPermissionQuery();

  const { internalVerificationStatus } = useUserStatus();

  // manual checking via ref if two-factor authentication is required
  const otpRequired = ref(false);

  const { mutate: tokenAuthenticateMutate, loading: tokenAuthenticateLoading } =
    useCustomMutation<TokenAuthenticate, TokenAuthenticateVariables>(
      tokenAuthenticateGql
    );

  const tokenAuthenticate = async (
    captcha?,
    showError?: boolean,
    username?: string,
    password?: string,
    code?: string,
    isLoginOtp = false,
    isRegister = false
  ) => {
    const tokenAuthenticateResponse = await tokenAuthenticateMutate({
      input: {
        username: username,
        password: password,
        /**
         * remove otp code spaces on submit, spaces comes from input formatting
         */
        code: code,
        ...insertItemsIf(captcha, { captcha }),
      },
    });

    const parsedGqlResponse =
      parseGqlResponse<TokenAuthenticate_tokenAuthenticate_Authentication>(
        "Authentication",
        tokenAuthenticateResponse,
        /** force to provide null error code
         * to show error toast for
         * Authentication Two Factor & Invalid Input
         * see: parseGqlResponse - hideIfErrorCode param
         * & errorHandler.ts
         */
        showError ? apiErrorCodes.INTERNAL_ERROR : HideIfErrorCode.ALL_ERRORS
      );

    // pass value to simple store
    loginSimpleStore.username = username;
    loginSimpleStore.password = password;
    loginSimpleStore.captcha = captcha;

    /**
     * If User entered a wrong OTP on LoginOTP, we must show the error
     * on the otp form and must not enter on this conditional checking
     *
     * This must enter only on Login state and not on Login OTP state
     */
    if (
      find(parsedGqlResponse?.error?.errors, [
        "code",
        apiErrorCodes.TWO_FACTOR_AUTH,
      ]) &&
      !isLoginOtp
    ) {
      // if certain error code is detected from errors,
      // toggle otpRequired to view LoginOTP Layout
      otpRequired.value = true;

      const errors = parsedGqlResponse.error
        ?.errors[0] as unknown as TokenAuthenticate_tokenAuthenticate_ResponseErrors_errors_InvalidOtp;

      // assign updated data to vuex store for security protocols
      await store.dispatch("loginOtpUpdate", {
        isPhoneConfirmed: errors?.isPhoneConfirmed || false,
        isEmailConfirmed: errors?.isEmailConfirmed || false,
        hasBackupCodesSetup: errors?.hasBackupCodesSetup || false,
      });
    } else if (parsedGqlResponse.data?.token && parsedGqlResponse.data?.user) {
      await store.dispatch("loginSuccess", {
        token: parsedGqlResponse.data?.token,
        ...parsedGqlResponse.data?.user,
        mobile: parsedGqlResponse.data?.user.phone,
        idDocument: {
          hasError:
            parsedGqlResponse.data?.verificationRequirementsStatus?.idDocument
              ?.hasError,
          verificationStatus:
            parsedGqlResponse.data?.verificationRequirementsStatus?.idDocument
              ?.verificationStatus,
        },
        addressDocument: {
          hasError:
            parsedGqlResponse.data?.verificationRequirementsStatus
              ?.addressDocument?.hasError,
          verificationStatus:
            parsedGqlResponse.data?.verificationRequirementsStatus
              ?.addressDocument?.verificationStatus,
        },
        selfie: {
          hasError:
            parsedGqlResponse.data?.verificationRequirementsStatus?.selfie
              ?.hasError,
          verificationStatus:
            parsedGqlResponse.data?.verificationRequirementsStatus?.selfie
              ?.verificationStatus,
        },
        questionnaire: {
          hasError:
            parsedGqlResponse.data?.verificationRequirementsStatus
              ?.questionnaire?.hasError,
          verificationStatus:
            parsedGqlResponse.data?.verificationRequirementsStatus
              ?.questionnaire?.verificationStatus,
        },
        companyDocuments: {
          hasError:
            parsedGqlResponse.data?.verificationRequirementsStatus
              ?.companyDocuments?.hasError,
          documents:
            parsedGqlResponse.data?.verificationRequirementsStatus
              ?.companyDocuments?.documents,
        },
      });

      /**
       * On successful Login, call the fetchAccountPermission
       * so the response will be saved to vuex store
       * declared here since after this line of code,
       * the router will be redirected to dashboard
       */
      await fetchAccountPermission();

      const isEmailConfirmed = parsedGqlResponse.data?.user?.isEmailConfirmed;
      const isPhoneConfirmed = parsedGqlResponse.data?.user?.isPhoneConfirmed;

      // For Login components, check if user must be redirected to register with pending confirmation or not
      if (!isRegister) {
        // Check if the user is still on register phase and if user
        // is not yet email/phone confirmed, redirect to Register
        if (
          internalVerificationStatus == VerificationStatus.NOT_VERIFIED &&
          (!isEmailConfirmed || !isPhoneConfirmed)
        ) {
          await router.push({ name: routeNames.register });
        }
        // else, redirect directly to dashboard after logging in
        await router.push({ name: routeNames.home });
      }

      // manual resetting of ref flag
      otpRequired.value = false;

      // reset store
      loginSimpleStore.resetStore();
    } else if (parsedGqlResponse?.error) {
      // throw error here so we can catch on parent component and pass the error below the field
      throw parsedGqlResponse.error;
    } else {
      // fallback throw error to parent component on token authenticate empty response
      throw new Error(
        "Error on fetching token authenticate. Please check your internet connection."
      );
    }
  };

  return {
    tokenAuthenticateLoading: computed(
      () => tokenAuthenticateLoading.value || accountPermissionLoading.value
    ),
    tokenAuthenticate,
    otpRequired,
  };
}
